import { useDeviceType } from '@talos/kyoko';
import { useParams } from 'react-router-dom';
import { selectLayoutSection } from '../components/AppLayout/AppLayoutSlice';
import { DEFAULT_LAYOUT_ID } from '../components/AppLayout/Layouts/tokens';
import { getLayoutIdForDevice } from '../components/AppLayout/utils/getLayoutIdForDevice';
import { isUserLayoutId } from '../components/AppLayout/utils/userLayoutConfig';
import { useAppLayoutConfig } from '../providers/AppLayoutConfig/AppLayoutConfigContext';
import { useAppStateSelector } from '../providers/AppStateProvider';
import type { LayoutSection, LayoutSpec } from '../types/LayoutConfig';
import { useFeatureFlag } from './useFeatureFlag';

/**
 * Returns the persisted selected layout id for the section (from app config), or the default layout id.
 * Use when you need the selection outside `AppLayout` route context (which also syncs selection from the URL).
 * @param section - The section to get the layout ID for
 * @returns The last selected layout ID for the given section
 */
export function useAppLayoutId(section: LayoutSection) {
  const { selectedLayoutId, layoutConfigsMap } = useAppLayoutConfig(section);
  const fallbackLayoutId = Object.keys(layoutConfigsMap ?? {})[0] ?? DEFAULT_LAYOUT_ID;
  const layoutId = selectedLayoutId ?? fallbackLayoutId;
  if (!isUserLayoutId(layoutId, layoutConfigsMap)) {
    return fallbackLayoutId;
  }
  return layoutId;
}

/** Returns the last selected layout ID for the current layout section. Defaults to 'trading' if layout section is not set. */
export function useCurrentAppLayoutId() {
  const layoutSection = useAppStateSelector(selectLayoutSection);
  return useAppLayoutId(layoutSection);
}

/**
 * Get the layout ID that best matches the current device type among the user's persisted layouts only.
 * Tries exact device type first, then larger sizes (e.g. tablet → laptop → desktop → widescreen),
 * then 'all'. Falls back to selected layout ID or default when no match is found.
 */
export function useLayoutIdForDevice(section: LayoutSection) {
  const { selectedLayoutId, layoutConfigsMap } = useAppLayoutConfig(section);
  const deviceType = useDeviceType();
  const sectionLayouts = layoutConfigsMap ?? {};
  const layoutIdForDevice = getLayoutIdForDevice(sectionLayouts, deviceType, selectedLayoutId ?? undefined);
  return layoutIdForDevice ?? selectedLayoutId ?? DEFAULT_LAYOUT_ID;
}

/**
 * Layout id used for index redirects and replacing unknown layout segments in the URL.
 * When spaces are enabled for the section, uses device-selected layout; otherwise last selected.
 */
export function useCanonicalLayoutIdForSection(section: LayoutSection) {
  const { enableTradingSpaces, enableMonitoringSpaces } = useFeatureFlag();
  const selectedLayoutId = useAppLayoutId(section);
  const layoutIdForDevice = useLayoutIdForDevice(section);
  const spacesEnabled = section === 'trading' ? enableTradingSpaces : enableMonitoringSpaces;
  return spacesEnabled ? layoutIdForDevice : selectedLayoutId;
}

/**
 * Resolves the `LayoutSpec` for the current URL via a direct `:layoutId` → config map lookup. Returns
 * `undefined` when the URL segment is missing or unknown; unknown / stale ids should be rewritten by
 * {@link useRedirectSpacesRoute} rather than silently substituted here.
 */
export function useAppLayoutForRouteParams(section: LayoutSection): LayoutSpec | undefined {
  const { layoutConfigsMap } = useAppLayoutConfig(section);
  const { layoutId } = useParams<{ layoutId?: string }>();
  return layoutId != null ? layoutConfigsMap?.[layoutId] : undefined;
}
